import { Button, Input, Loader } from "@mantine/core";
import {
  Checkbox,
  Divider,
  Dropdown,
  Modal,
  Popover,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  notification,
} from "antd";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { allOrThrow, single } from "@/lib/orpc-batch";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CSS } from "@dnd-kit/utilities";
import type { DragEndEvent } from "@dnd-kit/core";
import type { SelectProps } from "antd";
import dayjs from "dayjs";
import { findMatchingPdf as findMatchingDrawingPdf } from "@/utility/drawingPdf";
import { orpcClient } from "@/lib/orpc-client";
// 3D 改吃 project.getIfc 的分析結果（Part[] JSON），不再從列上/附件找 .ifc
// import { resolveIfcModel } from "@/utility/ifcModel";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useFeature } from "@/config/useFeature";
import { useIfcPreview } from "@/components/IfcViewerModal";
import { useParams } from "react-router-dom";
import { usePdfPreview } from "@/components/PdfPreviewModal";
import { useTranslation } from "react-i18next";

const IFC_STATUS_PROCESSING = "processing";
const IFC_STATUS_DONE = "done";
const IFC_STATUS_FAILED = "failed";
const IFC_POLL_INTERVAL_MS = 4000;
const IFC_POLL_MAX_ATTEMPTS = 150;

type IfcAnalysisState = {
  status: "" | "processing" | "done" | "failed";
  url?: string;
  error?: string;
};

interface DraggableHeaderCellProps
  extends React.HTMLAttributes<HTMLTableCellElement> {
  id?: string;
}

const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({
  id,
  ...restProps
}) =>
  id ? <SortableHeaderCell id={id} {...restProps} /> : <th {...restProps} />;

const SortableHeaderCell: React.FC<
  DraggableHeaderCellProps & { id: string }
> = ({ id, children, ...restProps }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    ...restProps.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "move",
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <th
      {...restProps}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </th>
  );
};

function DesignBOM() {
  const { t } = useTranslation();
  const [fileUpload, setFileUpload] = useState<any[]>([]);
  const [designBOM, setDesignBOM] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editRowData, setEditRowData] = useState([]);
  const [param1Type1, setParam1Type1] = useState("");
  const [param1Type2, setParam1Type2] = useState("");
  const [param1Type3, setParam1Type3] = useState("");
  const [param2Type1, setParam2Type1] = useState("");
  const [param2Type2, setParam2Type2] = useState("");
  const [param2Type3, setParam2Type3] = useState("");
  const [param2Type4, setParam2Type4] = useState("");
  const [param2Type5, setParam2Type5] = useState("");
  const [printQRCode, setPrintQRCode] = useState(false);
  const [param1Char1, setParam1Char1] = useState("");
  const [param1Char2, setParam1Char2] = useState("");
  const [param1Char3, setParam1Char3] = useState("");
  const [param2Char1, setParam2Char1] = useState("");
  const [param2Char2, setParam2Char2] = useState("");
  const [param2Char3, setParam2Char3] = useState("");
  const [param2Char4, setParam2Char4] = useState("");
  const [param2Char5, setParam2Char5] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    guandaId: "",
    steelAssemblyCode: "",
    distinction: "",
    transportNumber: "",
    metricSizeMark: "",
  });

  const [isParameterModalOpen, setIsParameterModalOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isManualBOMModalOpen, setIsManualBOMModalOpen] = useState(false);
  const [manualBOMForm, setManualBOMForm] = useState({
    steelAssemblyId: "",
    steelAssemblyCode: "",
    installationLocation: "",
    installationElevation: "",
    distinction: "",
    transportNumber: "",
    transportDescription: "",
    drawingName: "",
    totalWidth: "",
    totalHeight: "",
    totalLength: "",
    totalWeight: "",
    totalArea: "",
    metricSizeMark: "",
  });

  const [deleteFileUpload, setDeleteFileUpload] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [allData, setAllData] = useState([]);
  const [projectAttachments, setProjectAttachments] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<string>("");
  const [ifcUploading, setIfcUploading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [ifcAnalysis, setIfcAnalysis] = useState<IfcAnalysisState>({
    status: "",
  });
  const ifcInputRef = useRef<HTMLInputElement | null>(null);
  const ifcPollAbortRef = useRef(false);
  const { openPdf, pdfPreview } = usePdfPreview();
  const { openIfc, ifcPreview } = useIfcPreview();
  const show3dColumn = useFeature("designBom3dView");
  const ifcPartsUrl =
    ifcAnalysis.status === IFC_STATUS_DONE ? (ifcAnalysis.url ?? null) : null;
  const [api, contextHolder] = notification.useNotification();
  console.log("deleteFileUpload", deleteFileUpload);
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);
  const fileUploadRef = useRef(fileUpload);

  const COLUMN_ORDER_STORAGE_KEY = `designBOM_columnOrder_${id}`;
  const COLUMN_VISIBILITY_STORAGE_KEY = `designBOM_columnVisibility_${id}`;

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const getFiltersFromURL = () => {
    return {
      guandaId: searchParams.get("guandaId") || "",
      steelAssemblyCode: searchParams.get("steelAssemblyCode") || "",
      distinction: searchParams.get("distinction") || "",
      transportNumber: searchParams.get("transportNumber") || "",
      metricSizeMark: searchParams.get("metricSizeMark") || "",
    };
  };

  const getCurrentPageFromURL = () => {
    const page = searchParams.get("page");
    return page ? parseInt(page, 10) : 1;
  };

  const getPageSizeFromURL = () => {
    const size = searchParams.get("pageSize");
    return size ? parseInt(size, 10) : 25;
  };

  const updateURL = (newFilters?: any, page?: number, size?: number) => {
    const params = new URLSearchParams(searchParams);

    if (newFilters !== undefined) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== "") {
          params.set(key, value as string);
        } else {
          params.delete(key);
        }
      });
    }

    if (page !== undefined) {
      if (page > 1) {
        params.set("page", page.toString());
      } else {
        params.delete("page");
      }
    }

    if (size !== undefined && size !== 25) {
      params.set("pageSize", size.toString());
    } else if (size === 25) {
      params.delete("pageSize");
    }

    setSearchParams(params);
  };

  const handleSelectAll = useCallback(
    async (selected: boolean, selectedRows: any, changeRows: any) => {
      if (selected) {
        try {
          if (!id) return;

          const allDataResult = await orpcClient.steelAssembly.list({
            filter: { buildingId: id },
          });

          if (allDataResult) {
            let filteredData = allDataResult.filter((item) => {
              const steelAssembly = item;
              if (!steelAssembly) return false;

              if (
                filters.guandaId &&
                !steelAssembly.guandaId
                  ?.toString()
                  .includes(filters.guandaId.trim())
              ) {
                return false;
              }

              if (
                filters.steelAssemblyCode &&
                !steelAssembly.steelAssemblyCode
                  ?.toLowerCase()
                  .includes(filters.steelAssemblyCode.toLowerCase().trim())
              ) {
                return false;
              }

              if (
                filters.distinction &&
                !steelAssembly.distinction
                  ?.toLowerCase()
                  .includes(filters.distinction.toLowerCase().trim())
              ) {
                return false;
              }

              if (
                filters.transportNumber &&
                !steelAssembly.transportNumber
                  ?.toLowerCase()
                  .includes(filters.transportNumber.toLowerCase().trim())
              ) {
                return false;
              }

              if (
                filters.metricSizeMark &&
                !steelAssembly.metricSizeMark
                  ?.toLowerCase()
                  .includes(filters.metricSizeMark.toLowerCase().trim())
              ) {
                return false;
              }

              return true;
            });

            setAllData(filteredData);
            const allKeys = filteredData.map((item) => item.id);
            setSelectedRowKeys(allKeys);
            setDeleteFileUpload(filteredData);
          }
        } catch (error) {
          console.error("Error fetching all data:", error);
          const currentPageKeys = fileUploadRef.current.map((item) => item.id);
          setSelectedRowKeys((prev) => [
            ...new Set([...prev, ...currentPageKeys]),
          ]);

          setDeleteFileUpload((prev) => {
            const allSelectedRows = [
              ...prev.filter((item) => !currentPageKeys.includes(item.id)),
              ...fileUploadRef.current,
            ];
            return allSelectedRows;
          });
        }
      } else {
        setSelectedRowKeys([]);
        setDeleteFileUpload([]);
        setAllData([]);
      }
    },
    [id, filters],
  );

  const handleSelectionChange = useCallback(
    (selectedKeys: any, selectedRows: any) => {
      setSelectedRowKeys(selectedKeys);

      setDeleteFileUpload((prevDeleteFileUpload: any) => {
        const currentPageIds = fileUploadRef.current.map((item) => item.id);
        const otherPagesSelected = prevDeleteFileUpload.filter(
          (item) => !currentPageIds.includes(item.id),
        );

        const currentPageSelected = fileUploadRef.current.filter((item) =>
          selectedKeys.includes(item.id),
        );

        return [...otherPagesSelected, ...currentPageSelected];
      });
    },
    [],
  );

  const rowSelection = useMemo(
    () => ({
      type: "checkbox" as const,
      fixed: "left" as const,
      selectedRowKeys: selectedRowKeys,
      preserveSelectedRowKeys: true,
      onSelectAll: handleSelectAll,
      onChange: handleSelectionChange,
      getCheckboxProps: (record: any) => ({
        disabled: record?.name === "Disabled User",
        name: record?.name,
      }),
    }),
    [selectedRowKeys, handleSelectAll, handleSelectionChange],
  );

  const handleChangePayableDetail = (
    documentId: any,
    value: any,
    type: any,
    i: any,
  ) => {
    setEditRowData((prev: any) => ({
      ...prev,
      [type]: typeof value === "string" ? value?.trim() : value,
    }));
  };

  const rowEditDisplay = (component: any, i: any, input: any) => {
    return editRowId === i?.id ? component : <div>{input}</div>;
  };

  // const handleChangeCSV = (info, setFileUpload) => {
  //   const file = info.file.originFileObj;
  //   if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
  //     const reader = new FileReader();

  //     reader.onload = (e) => {
  //       const data = new Uint8Array(e.target.result);
  //       const workbook = XLSX.read(data, { type: 'array' });
  //       const sheetName = workbook.SheetNames[0]; // Read the first sheet
  //       const worksheet = workbook.Sheets[sheetName];
  //       const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' }); // Convert to JSON

  //       const filteredData = jsonData.filter((item) => {
  //         let part = Object.values(item)[0];
  //         return part === 'SteelAssembly';
  //       });

  //       const formattedData = filteredData.map((item, index) => ({
  //         key: index,
  //         name: item?.新建工程 || '',
  //         b: item?.__EMPTY || '',
  //         c: item?.__EMPTY_1 || '',
  //         d: item?.__EMPTY_2 || '',
  //         e: item?.__EMPTY_3 || '',
  //         f: item?.__EMPTY_4 || '',
  //         g: item?.__EMPTY_5 || '',
  //         h: item?.__EMPTY_6 || '',
  //         i: item?.__EMPTY_7 || '',
  //         j: item?.__EMPTY_8 || '',
  //         k: item?.__EMPTY_9 || '',
  //         l: item?.__EMPTY_10 || '',
  //         m: item?.__EMPTY_11 || '',
  //         n: item?.__EMPTY_12 || '',
  //       }));

  //       setFileUpload(formattedData);
  //     };

  //     reader.readAsArrayBuffer(file); // Read file as binary buffer
  //   } else {
  //     setFileUpload((prev) => [...prev, { id: Date.now(), name: file.name }]);
  //   }
  // };

  const baseColumns = [
    {
      key: "steelAssemblyId",
      title: t("designBOM.componentId") || "構件ID",
      dataIndex: "steelAssemblyId",
      hidden: hiddenColumns.includes("steelAssemblyId"),
      render: (text: any, i: any) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.steelAssemblyId?.trim()}
            // disabled={editRowId !== i?.id}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "steelAssemblyId",
                i,
              )
            }
          />,
          i,
          i?.steelAssemblyId,
        );
      },
    },
    {
      key: "guandaId",
      title: t("designBOM.guandaId") || "廣達ID",
      dataIndex: "guandaId",
      hidden: hiddenColumns.includes("guandaId"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            value={editRowData?.guandaId?.toString()?.trim() || ""}
            onChange={(e) => {
              const value = e.target.value.trim();
              const numericValue = value === "" ? null : parseInt(value, 10);
              handleChangePayableDetail(
                i?.documentId || i?.key,
                numericValue,
                "guandaId",
                i,
              );
            }}
          />,
          i,
          i?.guandaId?.toString().padStart(7, "0"),
        );
      },
    },
    {
      key: "steelAssemblyCode",
      title: t("designBOM.componentCode") || "構件編號",
      dataIndex: "steelAssemblyCode",
      hidden: hiddenColumns.includes("steelAssemblyCode"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.steelAssemblyCode?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "steelAssemblyCode",
                i,
              )
            }
          />,
          i,
          i?.steelAssemblyCode,
        );
      },
    },
    {
      key: "installationLocation",
      title: t("designBOM.installationLocation") || "安裝位置",
      dataIndex: "installationLocation",
      hidden: hiddenColumns.includes("installationLocation"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.installationLocation?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "installationLocation",
                i,
              )
            }
          />,
          i,
          i?.installationLocation,
        );
      },
    },
    {
      key: "installationElevation",
      title: t("designBOM.installationElevation") || "安裝高程",
      dataIndex: "installationElevation",
      hidden: hiddenColumns.includes("installationElevation"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.installationElevation}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "installationElevation",
                i,
              )
            }
          />,
          i,
          i?.installationElevation,
        );
      },
    },
    {
      key: "distinction",
      title: t("designBOM.distinction") || "區別",
      dataIndex: "distinction",
      hidden: hiddenColumns.includes("distinction"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.distinction?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "distinction",
                i,
              )
            }
          />,
          i,
          i?.distinction,
        );
      },
    },
    {
      key: "transportNumber",
      title: t("designBOM.transportNumber") || "拆運號碼",
      dataIndex: "transportNumber",
      hidden: hiddenColumns.includes("transportNumber"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.transportNumber?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "transportNumber",
                i,
              )
            }
          />,
          i,
          i?.transportNumber,
        );
      },
    },
    {
      key: "transportDescription",
      title: t("designBOM.transportDescription") || "拆運說明",
      dataIndex: "transportDescription",
      hidden: hiddenColumns.includes("transportDescription"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.transportDescription?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "transportDescription",
                i,
              )
            }
          />,
          i,
          i?.transportDescription,
        );
      },
    },
    {
      key: "drawingName",
      title: t("designBOM.drawingName") || "圖面名稱",
      dataIndex: "drawingName",
      hidden: hiddenColumns.includes("drawingName"),
      render: (text, i) => {
        const matchingPdf = findMatchingPdf(i?.drawingName);
        const displayContent = matchingPdf ? (
          <a
            title={matchingPdf.name}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openPdf(matchingPdf);
            }}
            style={{
              color: "#1890ff",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {i?.drawingName}
          </a>
        ) : (
          i?.drawingName
        );
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            value={editRowData?.drawingName?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "drawingName",
                i,
              )
            }
          />,
          i,
          displayContent,
        );
      },
    },
    ...(show3dColumn
      ? [
          {
            key: "model3d",
            title: t("designBOM.model3d") || "3D圖",
            dataIndex: "model3d",
            hidden: hiddenColumns.includes("model3d"),
            render: (text: any, i: any) => {
              const marks = [
                i?.steelAssemblyCode?.toString(),
                i?.steelAssemblyId?.toString(),
              ];
              return (
                <a
                  title={marks.find((mark) => mark?.trim())?.trim() || undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openIfc(ifcPartsUrl, marks);
                  }}
                  style={{
                    color: ifcPartsUrl ? "#1890ff" : "#8c8c8c",
                    textDecoration: ifcPartsUrl ? "underline" : "none",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ifcPartsUrl
                    ? t("designBOM.view3d") || "3D 檢視"
                    : t("designBOM.pick3dFile") || "選檔"}
                </a>
              );
            },
          },
        ]
      : []),
    {
      key: "totalWidth",
      title: t("designBOM.totalWidth") || "總寬度",
      dataIndex: "totalWidth",
      hidden: hiddenColumns.includes("totalWidth"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalWidth}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalWidth",
                i,
              )
            }
          />,
          i,
          i?.totalWidth,
        );
      },
    },
    {
      key: "totalHeight",
      title: t("designBOM.totalHeight") || "總高度",
      dataIndex: "totalHeight",
      hidden: hiddenColumns.includes("totalHeight"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalHeight}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalHeight",
                i,
              )
            }
          />,
          i,
          i?.totalHeight,
        );
      },
    },
    {
      key: "totalLength",
      title: t("designBOM.totalLength") || "總長度",
      dataIndex: "totalLength",
      hidden: hiddenColumns.includes("totalLength"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalLength}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalLength",
                i,
              )
            }
          />,
          i,
          i?.totalLength,
        );
      },
    },
    {
      key: "totalWeight",
      title: t("designBOM.totalWeight") || "總重量",
      dataIndex: "totalWeight",
      hidden: hiddenColumns.includes("totalWeight"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalWeight}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalWeight",
                i,
              )
            }
          />,
          i,
          i?.totalWeight,
        );
      },
    },
    {
      key: "totalArea",
      title: t("designBOM.totalArea") || "總面積",
      dataIndex: "totalArea",
      hidden: hiddenColumns.includes("totalArea"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.totalArea}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "totalArea",
                i,
              )
            }
          />,
          i,
          i?.totalArea,
        );
      },
    },
    {
      key: "metricSizeMark",
      title: t("designBOM.sectionSpecification") || "斷面規格",
      dataIndex: "metricSizeMark",
      hidden: hiddenColumns.includes("metricSizeMark"),
      render: (text, i) => {
        return rowEditDisplay(
          <Input
            placeholder="Input component"
            //leftSection={<IconMail size={16} />}
            value={editRowData?.metricSizeMark?.trim()}
            onChange={(e) =>
              handleChangePayableDetail(
                i?.documentId || i?.key,
                e.target.value,
                "metricSizeMark",
                i,
              )
            }
          />,
          i,
          i?.metricSizeMark,
        );
      },
    },
    {
      key: "actions",
      title: "",
      fixed: "right",
      hidden: hiddenColumns.includes("actions"),
      render: (text, i) => {
        return (
          <>
            {editRowId ? (
              <>
                <Button
                  variant="default"
                  style={{ marginRight: "15px" }}
                  onClick={() => {
                    setEditRowId(null);
                    setEditRowData([]);
                  }}
                >
                  {t("designBOM.cancel") || "取消"}
                </Button>
                <Button
                  variant="default"
                  onClick={async (element: any) => {
                    try {
                      if (!id) return;

                      const result = await single(
                        orpcClient.steelAssembly.update([
                          {
                            id: editRowData?.id,
                            steelAssemblyId: String(
                              editRowData?.steelAssemblyId?.trim(),
                            ),
                            steelAssemblyCode:
                              editRowData?.steelAssemblyCode?.trim(),
                            installationLocation:
                              editRowData?.installationLocation?.trim(),
                            installationElevation:
                              editRowData?.installationElevation,
                            distinction: String(
                              editRowData?.distinction?.trim(),
                            ),
                            transportNumber:
                              editRowData?.transportNumber?.trim(),
                            transportDescription:
                              editRowData?.transportDescription?.trim(),
                            drawingName: editRowData?.drawingName?.trim(),
                            totalWidth: editRowData?.totalWidth,
                            totalHeight: editRowData?.totalHeight,
                            totalLength: editRowData?.totalLength,
                            totalWeight: editRowData?.totalWeight,
                            totalArea: editRowData?.totalArea,
                            metricSizeMark: editRowData?.metricSizeMark?.trim(),
                          },
                        ]),
                      );

                      if (result) {
                        api.success({
                          message: `Notification `,
                          description: (
                            <div>
                              {t("designBOM.editSuccess") || "編輯成功"}
                            </div>
                          ),
                          placement: "topLeft",
                          duration: 1.5,
                        });
                      }
                    } catch (error) {
                      api.error({
                        message: `Notification `,
                        description: (
                          <div>{t("designBOM.editFailed") || "編輯失敗"}</div>
                        ),
                        placement: "topLeft",
                        duration: 1.5,
                      });
                    }

                    setCurrentPage(1);
                    updateURL(undefined, 1);
                    fetchData(1, pageSize, false, filters);
                    setEditRowId(null);
                    setEditRowData([]);
                  }}
                >
                  {t("designBOM.save") || "儲存"}
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                style={{ marginRight: "15px" }}
                onClick={() => {
                  setEditRowId(i?.id);
                  setEditRowData(i);
                }}
              >
                {t("designBOM.edit") || "編輯"}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  const fetchData = async (
    page = 1,
    currentPageSize = pageSize,
    clearSelection = false,
    customFilters = filters,
  ) => {
    if (!id) return;
    setTableLoading(true);
    try {
      let allData = await orpcClient.steelAssembly.list({
        filter: { buildingId: id },
      });

      let filteredData = allData.filter((item) => {
        const steelAssembly = item;
        if (!steelAssembly) return false;

        console.log("steelAssembly", steelAssembly);

        if (
          customFilters.guandaId &&
          !steelAssembly.guandaId
            ?.toString()
            .includes(customFilters.guandaId.trim())
        ) {
          return false;
        }

        if (
          customFilters.steelAssemblyCode &&
          !steelAssembly.steelAssemblyCode
            ?.toLowerCase()
            .includes(customFilters.steelAssemblyCode.toLowerCase().trim())
        ) {
          return false;
        }

        if (
          customFilters.distinction &&
          !steelAssembly.distinction
            ?.toLowerCase()
            .includes(customFilters.distinction.toLowerCase().trim())
        ) {
          return false;
        }

        if (
          customFilters.transportNumber &&
          !steelAssembly.transportNumber
            ?.toLowerCase()
            .includes(customFilters.transportNumber.toLowerCase().trim())
        ) {
          return false;
        }

        if (
          customFilters.metricSizeMark &&
          !steelAssembly.metricSizeMark
            ?.toLowerCase()
            .includes(customFilters.metricSizeMark.toLowerCase().trim())
        ) {
          return false;
        }

        return true;
      });

      const totalCount = filteredData.length;
      const startIndex = (page - 1) * currentPageSize;
      const endIndex = startIndex + currentPageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      console.log("paginatedData", paginatedData);

      setFileUpload(paginatedData);
      setDesignBOM({
        meta: {
          pagination: {
            total: totalCount,
            page,
            pageSize: currentPageSize,
            pageCount: Math.ceil(totalCount / currentPageSize),
          },
        },
      } as any);

      if (clearSelection) {
        setSelectedRowKeys([]);
        setDeleteFileUpload([]);
        setAllData([]);
      }
    } catch (error) {
      console.error("Error fetching department data:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const pollIfcAnalysis = useCallback(async (targetProjectId: string) => {
    ifcPollAbortRef.current = false;

    for (let attempt = 0; attempt < IFC_POLL_MAX_ATTEMPTS; attempt++) {
      if (ifcPollAbortRef.current) return;

      try {
        const result: any = await orpcClient.project.getIfc({
          id: targetProjectId,
        });
        if (ifcPollAbortRef.current) return;

        if (result?.status === IFC_STATUS_DONE) {
          setIfcAnalysis({ status: IFC_STATUS_DONE, url: result.url });
          return;
        }
        if (result?.status === IFC_STATUS_FAILED) {
          setIfcAnalysis({
            status: IFC_STATUS_FAILED,
            error: result.errorMessage || undefined,
          });
          return;
        }
        setIfcAnalysis({ status: IFC_STATUS_PROCESSING });
      } catch (error) {
        if (ifcPollAbortRef.current) return;
        console.error("Error polling ifc analysis:", error);
        setIfcAnalysis({
          status: IFC_STATUS_FAILED,
          error: (error as any)?.message || undefined,
        });
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, IFC_POLL_INTERVAL_MS));
    }
  }, []);

  useEffect(() => {
    return () => {
      ifcPollAbortRef.current = true;
    };
  }, []);

  const fetchProjectAttachments = async () => {
    if (!id) return;
    try {
      const projects = await orpcClient.project.list({});
      const project = projects.find((p: any) =>
        p.projectNode?.some((node: any) => node.id === id),
      );
      if (project?.id) setProjectId(project.id);
      if (project?.attachment) {
        setProjectAttachments(project.attachment as any[]);
      }
      const savedStatus = (project as any)?.ifcAnalysisStatus;
      if (savedStatus) {
        setIfcAnalysis({
          status: savedStatus,
          error: (project as any)?.ifcAnalysisError || undefined,
        });
        if (
          (savedStatus === IFC_STATUS_PROCESSING ||
            savedStatus === IFC_STATUS_DONE) &&
          project?.id
        ) {
          void pollIfcAnalysis(project.id);
        }
      }
    } catch (error) {
      console.error("Error fetching project attachments:", error);
    }
  };

  const handleUploadIfc = async (file: File) => {
    if (!projectId) {
      api.error({
        message: t("designBOM.uploadIfcError") || "IFC 上傳失敗",
        description:
          t("designBOM.projectNotFound") || "找不到這個構件所屬的專案",
        placement: "topLeft",
      });
      return;
    }

    setIfcUploading(true);
    try {
      await orpcClient.project.uploadIfc({ id: projectId, file });
      setIfcAnalysis({ status: IFC_STATUS_PROCESSING });
      void pollIfcAnalysis(projectId);
      api.success({
        message: t("designBOM.uploadIfcSuccess") || "IFC 上傳成功",
        description: file.name,
        placement: "topLeft",
        duration: 1.5,
      });
    } catch (error) {
      console.error("Error uploading ifc:", error);
      setIfcAnalysis({
        status: IFC_STATUS_FAILED,
        error: (error as any)?.message || undefined,
      });
      api.error({
        message: t("designBOM.uploadIfcError") || "IFC 上傳失敗",
        description: (error as any)?.message || file.name,
        placement: "topLeft",
      });
    } finally {
      setIfcUploading(false);
    }
  };

  const findMatchingPdf = (drawingName: string) =>
    findMatchingDrawingPdf(projectAttachments, drawingName);

  const handleDeleteFileUpload = async (deleteFileUpload: any) => {
    if (!deleteFileUpload?.length) {
      return api.error({
        message: `Notification `,
        description: (
          <div>{t("designBOM.noItemsToDelete") || "請勾選需要刪除的項目"}</div>
        ),
        placement: "topLeft",
        duration: 1.5,
      });
    }
    if (!id) return;
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await allOrThrow(
        orpcClient.steelAssembly.delete(
          deleteFileUpload.map((element: any) => ({ id: element?.id })),
        ),
      );

      setCurrentPage(1);
      updateURL(undefined, 1);
      fetchData(1, pageSize, true, filters);
      api.success({
        message: `Notification `,
        description: <div>{t("designBOM.deleteSuccess") || "刪除成功"}</div>,
        placement: "topLeft",
        duration: 1.5,
      });
    } catch (e) {
      console.warn("error", e);
      return e;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditFileUpload = async (editFileUpload: any) => {
    if (!editFileUpload?.length) {
      return api.error({
        message: `Notification `,
        description: (
          <div>{t("designBOM.noChangesToUpdate") || "沒有修改可以更新"}</div>
        ),
        placement: "topLeft",
        duration: 1.5,
      });
    }
    if (!id) return;

    try {
      const tempPayableList = await allOrThrow(
        orpcClient.steelAssembly.update(
          editFileUpload.map((element: any) => ({
            id: element?.id,
            steelAssemblyId: String(element?.steelAssemblyId),
            steelAssemblyCode: element?.steelAssemblyCode,
            installationLocation: element?.installationLocation,
            installationElevation: element?.installationElevation,
            distinction: String(element?.distinction),
            transportNumber: element?.transportNumber,
            transportDescription: element?.transportDescription,
            drawingName: element?.drawingName,
            totalWidth: element?.totalWidth,
            totalHeight: element?.totalHeight,
            totalLength: element?.totalLength,
            totalWeight: element?.totalWeight,
            totalArea: element?.totalArea,
            metricSizeMark: element?.metricSizeMark,
          })),
        ),
      );

      if (tempPayableList[0]) {
        return api.success({
          message: `Notification `,
          description: <div>{t("designBOM.editSuccess") || "編輯成功"}</div>,
          placement: "topLeft",
          duration: 1.5,
        });
      }
    } catch (e) {
      console.warn("error", e);
      return e;
    }
  };

  const handleParameterSubmit = async () => {
    const values = {
      parameter1: {
        type1: param1Type1,
        type2: param1Type2,
        type3: param1Type3,
        char1: param1Char1,
        char2: param1Char2,
        char3: param1Char3,
      },
      parameter2: {
        type1: param2Type1,
        type2: param2Type2,
        type3: param2Type3,
        type4: param2Type4,
        type5: param2Type5,
        char1: param2Char1,
        char2: param2Char2,
        char3: param2Char3,
        char4: param2Char4,
        char5: param2Char5,
      },
      parameter3: {
        printQRCode,
      },
    };

    setIsExportLoading(true);

    try {
      if (!deleteFileUpload?.length) {
        api.error({
          message: t("designBOM.exportFailed") || "匯出失敗",
          description:
            t("designBOM.noItemsToExport") || "請先勾選需要匯出的項目",
          placement: "topLeft",
          duration: 3,
        });
        setIsExportLoading(false);
        return;
      }

      const allDesignBOMs = deleteFileUpload;

      const transformedData = allDesignBOMs
        .map((t) => {
          return {
            Profile: null,
            OkNumber: 0,
            Number: 0,
            Kg: 0,
            Length: 0,
            Status: null,
            QrCode: printQRCode,
            PartNumber: [
              param2Type1 === "char"
                ? param2Char1?.trim()
                : param2Type1 && param2Type1 !== "none"
                  ? t?.[param2Type1]?.toString()?.trim()
                  : "",
              param2Type2 === "char"
                ? param2Char2?.trim()
                : param2Type2 && param2Type2 !== "none"
                  ? t?.[param2Type2]?.toString()?.trim()
                  : "",
              param2Type3 === "char"
                ? param2Char3?.trim()
                : param2Type3 && param2Type3 !== "none"
                  ? t?.[param2Type3]?.toString()?.trim()
                  : "",
              param2Type4 === "char"
                ? param2Char4?.trim()
                : param2Type4 && param2Type4 !== "none"
                  ? t?.[param2Type4]?.toString()?.trim()
                  : "",
              param2Type5 === "char"
                ? param2Char5?.trim()
                : param2Type5 && param2Type5 !== "none"
                  ? t?.[param2Type5]?.toString()?.trim()
                  : "",
            ]
              .filter((item) => item && item.length > 0)
              .join(" ")
              .trim(),
            Index: 0,
            NewDataTime: null,
            ModifyDataTime: null,
            PartPath: null,
            BeatNumber: null,
            QrCodeContent: [String(t?.guandaId || "0").padStart(7, "0")],
            TrainNumber: [
              [
                param1Type1 === "char"
                  ? param1Char1?.trim()
                  : param1Type1 && param1Type1 !== "none"
                    ? t?.[param1Type1]?.toString()?.trim()
                    : "",
                param1Type2 === "char"
                  ? param1Char2?.trim()
                  : param1Type2 && param1Type2 !== "none"
                    ? t?.[param1Type2]?.toString()?.trim()
                    : "",
                param1Type3 === "char"
                  ? param1Char3?.trim()
                  : param1Type3 && param1Type3 !== "none"
                    ? t?.[param1Type3]?.toString()?.trim()
                    : "",
              ]
                .filter((item) => item && item.length > 0)
                .join(" ")
                .trim(),
            ],
            steelAssemblyId: t?.steelAssemblyId?.toString()?.trim() ?? "",
            guandaId: String(t?.guandaId || "0").padStart(7, "0"),
            steelAssemblyCode: t?.steelAssemblyCode?.toString()?.trim() ?? "",
            _originalData: t,
          };
        })
        .sort((a, b) => {
          const codeA = a._originalData?.steelAssemblyCode || "";
          const codeB = b._originalData?.steelAssemblyCode || "";
          return codeA.localeCompare(codeB);
        })
        .map(({ _originalData, ...item }) => item);

      // Create a blob from the transformed data
      const blob = new Blob([JSON.stringify(transformedData, null, 2)], {
        type: "application/json",
      });

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Set the file name with current date
      const date = dayjs().format("YYYY-MM-DD");
      link.download = `design_bom_${date}.json`;

      // Trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsParameterModalOpen(false);
    } catch (error) {
      console.error("Download all data error:", error);
      api.error({
        message: t("designBOM.downloadFailed") || "下載失敗",
        description:
          t("designBOM.cannotFetchData") || "無法獲取所有資料，請稍後再試",
      });
    } finally {
      setIsExportLoading(false);
    }
  };

  const clearModalData = () => {
    setParam1Type1("");
    setParam1Type2("");
    setParam1Type3("");
    setParam2Type1("");
    setParam2Type2("");
    setParam2Type3("");
    setParam2Type4("");
    setParam2Type5("");
    setPrintQRCode(false);
    setParam1Char1("");
    setParam1Char2("");
    setParam1Char3("");
    setParam2Char1("");
    setParam2Char2("");
    setParam2Char3("");
    setParam2Char4("");
    setParam2Char5("");
  };

  const handleModalClose = () => {
    clearModalData();
    setIsParameterModalOpen(false);
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    updateURL(newFilters, 1);

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for API call
    debounceTimeoutRef.current = setTimeout(() => {
      fetchData(1, pageSize, true, newFilters);
    }, 500); // 500ms delay
  };

  const clearFilters = () => {
    const clearedFilters = {
      guandaId: "",
      steelAssemblyCode: "",
      distinction: "",
      transportNumber: "",
      metricSizeMark: "",
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    updateURL(clearedFilters, 1);
    fetchData(1, pageSize, true, clearedFilters);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    updateURL(filters, 1);
    fetchData(1, pageSize, true, filters);
  };

  const clearManualBOMForm = () => {
    setManualBOMForm({
      steelAssemblyId: "",
      steelAssemblyCode: "",
      installationLocation: "",
      installationElevation: "",
      distinction: "",
      transportNumber: "",
      transportDescription: "",
      drawingName: "",
      totalWidth: "",
      totalHeight: "",
      totalLength: "",
      totalWeight: "",
      totalArea: "",
      metricSizeMark: "",
    });
  };

  const handleManualBOMModalClose = () => {
    clearManualBOMForm();
    setIsManualBOMModalOpen(false);
  };

  const handleManualBOMFormChange = (field: any, value: any) => {
    setManualBOMForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleManualBOMSubmit = async () => {
    try {
      if (!id) return;

      const steelAssemblyResult = await single(
        orpcClient.steelAssembly.create([
          {
            buildingId: id,
            steelAssemblyId: manualBOMForm.steelAssemblyId,
            steelAssemblyCode: manualBOMForm.steelAssemblyCode,
            installationLocation: manualBOMForm.installationLocation,
            installationElevation: manualBOMForm.installationElevation
              ? parseFloat(manualBOMForm.installationElevation)
              : null,
            distinction: manualBOMForm.distinction,
            transportNumber: manualBOMForm.transportNumber,
            transportDescription: manualBOMForm.transportDescription,
            drawingName: manualBOMForm.drawingName,
            totalWidth: manualBOMForm.totalWidth
              ? parseFloat(manualBOMForm.totalWidth)
              : null,
            totalHeight: manualBOMForm.totalHeight
              ? parseFloat(manualBOMForm.totalHeight)
              : null,
            totalLength: manualBOMForm.totalLength
              ? parseFloat(manualBOMForm.totalLength)
              : null,
            totalWeight: manualBOMForm.totalWeight
              ? parseFloat(manualBOMForm.totalWeight)
              : null,
            totalArea: manualBOMForm.totalArea
              ? parseFloat(manualBOMForm.totalArea)
              : null,
            metricSizeMark: manualBOMForm.metricSizeMark,
          },
        ]),
      );

      // const result = await single(
      //   orpcClient.steelAssembly.create([
      //     { buildingId: id, steelAssemblyId: steelAssemblyResult.id },
      //   ]),
      // );

      if (steelAssemblyResult) {
        api.success({
          message: `Notification`,
          description: (
            <div>{t("designBOM.manualAddSuccess") || "手動新增構件成功"}</div>
          ),
          placement: "topLeft",
          duration: 3,
        });
        setCurrentPage(1);
        updateURL(undefined, 1);
        fetchData(1, pageSize, true, filters);
        handleManualBOMModalClose();
      }
    } catch (error) {
      console.error("Manual BOM submission error:", error);
      api.error({
        message: `Notification`,
        description: (
          <div>
            {error?.message ||
              t("designBOM.manualAddError") ||
              "手動新增構件失敗，請稍後再試"}
          </div>
        ),
        placement: "topLeft",
        duration: 1.5,
      });
    }
  };

  const handleParamChange =
    (setter: (value: string) => void) => (value: string) => {
      setter(value);
    };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = columnOrder.indexOf(active.id as string);
      const overIndex = columnOrder.indexOf(over?.id as string);
      const newColumnOrder = arrayMove(columnOrder, activeIndex, overIndex);
      setColumnOrder(newColumnOrder);
      localStorage.setItem(
        COLUMN_ORDER_STORAGE_KEY,
        JSON.stringify(newColumnOrder),
      );
    }
  };

  const columns = columnOrder
    .map((key) => baseColumns.find((col) => col.key === key))
    .filter((col): col is (typeof baseColumns)[number] => col !== undefined)
    .map((col, index) => {
      const newCol = { ...col };
      if (index === 0) {
        newCol.fixed = "left" as const;
      } else {
        delete newCol.fixed;
      }
      return newCol;
    });

  const toggleableColumns = baseColumns.filter((col) => col.key !== "actions");

  const updateHiddenColumns = (next: string[]) => {
    setHiddenColumns(next);
    localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(next));
  };

  const reconcileColumnOrder = (saved: string[]) => {
    const baseKeys = baseColumns.map((col) => col.key);
    const next = saved.filter((key) => baseKeys.includes(key));

    baseKeys.forEach((key, index) => {
      if (next.includes(key)) return;

      let insertAt = next.length;
      for (let i = index - 1; i >= 0; i--) {
        const at = next.indexOf(baseKeys[i]);
        if (at !== -1) {
          insertAt = at + 1;
          break;
        }
      }
      next.splice(insertAt, 0, key);
    });

    return next;
  };

  useEffect(() => {
    const nextOrder = reconcileColumnOrder(columnOrder);
    const changed =
      nextOrder.length !== columnOrder.length ||
      nextOrder.some((key, index) => key !== columnOrder[index]);

    if (changed) {
      setColumnOrder(nextOrder);
      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(nextOrder));
    }
  }, []);

  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    setFilters(urlFilters);
    setCurrentPage(urlPage);
    setPageSize(urlPageSize);

    fetchData(urlPage, urlPageSize, true, urlFilters);
    fetchProjectAttachments();
  }, [id]);

  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    if (
      JSON.stringify(urlFilters) !== JSON.stringify(filters) ||
      urlPage !== currentPage ||
      urlPageSize !== pageSize
    ) {
      setFilters(urlFilters);
      setCurrentPage(urlPage);
      setPageSize(urlPageSize);
      fetchData(urlPage, urlPageSize, true, urlFilters);
    }
  }, [searchParams]);

  useEffect(() => {
    fileUploadRef.current = fileUpload;
  }, [fileUpload]);

  useEffect(() => {
    const handlePaginationInput = () => {
      const paginationInputs = document.querySelectorAll(
        ".ant-pagination-options .ant-select input",
      );
      paginationInputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        inputElement.readOnly = true;
        inputElement.style.cursor = "pointer";
        inputElement.style.pointerEvents = "none";
        inputElement.style.caretColor = "transparent";
        inputElement.style.userSelect = "none";
      });

      const selectors = document.querySelectorAll(
        ".ant-pagination-options .ant-select .ant-select-selector",
      );
      selectors.forEach((selector) => {
        (selector as HTMLElement).style.cursor = "pointer";
      });
    };

    const observer = new MutationObserver(handlePaginationInput);
    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = setTimeout(handlePaginationInput, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      // Clean up debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const bomTotalCount = designBOM?.meta?.pagination?.total || 0;
  const toolbarRef = useRef<HTMLDivElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const summaryMeasureRef = useRef<HTMLSpanElement>(null);
  const toolbarActionsRef = useRef<HTMLDivElement>(null);
  const [compactSummary, setCompactSummary] = useState(false);
  const selectedTotalWeight = deleteFileUpload
    .reduce((sum, item) => sum + (parseFloat(item?.totalWeight) || 0), 0)
    .toFixed(2);
  const summaryTotalText =
    t("designBOM.summaryTotal", { count: bomTotalCount }) ||
    `本專案共 ${bomTotalCount} 隻`;
  const summarySelectedText =
    t("designBOM.summarySelected", { count: deleteFileUpload.length }) ||
    `已選擇 ${deleteFileUpload.length} 隻`;
  const summaryWeightText =
    t("designBOM.summaryWeight", { weight: selectedTotalWeight }) ||
    `總重量 ${selectedTotalWeight} kg`;

  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    const deleteButton = deleteButtonRef.current;
    const measure = summaryMeasureRef.current;
    const actions = toolbarActionsRef.current;
    if (!toolbar || !deleteButton || !measure || !actions) return;

    const update = () => {
      const toolbarStyle = getComputedStyle(toolbar);
      const available =
        toolbar.clientWidth -
        parseFloat(toolbarStyle.paddingLeft) -
        parseFloat(toolbarStyle.paddingRight);
      const columnGap = parseFloat(toolbarStyle.columnGap) || 0;
      const leftGap =
        parseFloat(getComputedStyle(deleteButton.parentElement!).columnGap) ||
        0;
      const leftNeeded =
        deleteButton.offsetWidth +
        (parseFloat(getComputedStyle(deleteButton).marginRight) || 0) +
        leftGap +
        measure.offsetWidth;
      const actionGap = parseFloat(getComputedStyle(actions).columnGap) || 0;
      const actionWidths = Array.from(actions.children)
        .map((child) => (child as HTMLElement).offsetWidth)
        .filter((width) => width > 0);
      const actionsNeeded =
        actionWidths.reduce((sum, width) => sum + width, 0) +
        actionGap * Math.max(actionWidths.length - 1, 0);
      setCompactSummary(leftNeeded + columnGap + actionsNeeded > available);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(toolbar);
    observer.observe(measure);
    observer.observe(deleteButton);
    Array.from(actions.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [show3dColumn, ifcAnalysis.status, ifcUploading]);

  return (
    <div className="px-8 m-8">
      {contextHolder}
      {pdfPreview}
      {ifcPreview}
      {isDeleting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader size="xl" color="blue" />
        </div>
      )}
      <div
        ref={toolbarRef}
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2"
        style={{
          backgroundColor: "#ECECEC",
          marginBottom: "1rem",
          position: "relative",
        }}
      >
        <span
          ref={summaryMeasureRef}
          aria-hidden
          data-testid="designbom-summary-measure"
          style={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            fontSize: "14px",
            fontVariantNumeric: "tabular-nums",
            left: 0,
            top: 0,
          }}
        >
          {summaryTotalText} / {summarySelectedText} / {summaryWeightText}
        </span>
        <div className="flex flex-wrap justify-start items-center gap-2">
          <Button
            ref={deleteButtonRef}
            //onClick={() => navigate(`/hrMainPage/addSteelAssemblyBOM/${id}`)}
            variant="filled"
            style={{ marginRight: "8px" }}
            color="red"
            disabled={isDeleting}
            onClick={() => handleDeleteFileUpload(deleteFileUpload)}
          >
            {t("designBOM.deleteSelected") || "刪除勾選的項目"}
          </Button>
          {compactSummary ? (
            <Popover
              trigger={["hover", "click"]}
              placement="bottomLeft"
              content={
                <div
                  data-testid="designbom-summary-detail"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <span data-testid="designbom-summary-total">
                    {summaryTotalText}
                  </span>
                  <span data-testid="designbom-summary-selected">
                    {summarySelectedText}
                  </span>
                  <span data-testid="designbom-summary-weight">
                    {summaryWeightText}
                  </span>
                </div>
              }
            >
              <button
                type="button"
                data-testid="designbom-summary-trigger"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                  fontSize: "13px",
                  color: deleteFileUpload.length ? "#1c7ed6" : "#333",
                  backgroundColor: "#fff",
                  border: "1px solid #d9d9d9",
                  borderRadius: "999px",
                  padding: "2px 10px",
                  cursor: "pointer",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t("designBOM.summaryCompact", {
                  selected: deleteFileUpload.length,
                  total: bomTotalCount,
                }) || `已選 ${deleteFileUpload.length} / ${bomTotalCount}`}
                <span
                  aria-hidden
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "1px solid currentColor",
                    fontSize: "10px",
                    lineHeight: 1,
                  }}
                >
                  i
                </span>
              </button>
            </Popover>
          ) : (
            <span
              data-testid="designbom-summary-inline"
              style={{
                whiteSpace: "nowrap",
                fontSize: "14px",
                color: "#333",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {summaryTotalText} / {summarySelectedText} / {summaryWeightText}
            </span>
          )}
          {/* <Button onClick={() => handleEditFileUpload(editFileUpload)} variant="filled">
            確定編輯Excel
          </Button> */}
        </div>
        <div
          ref={toolbarActionsRef}
          className="flex flex-wrap items-center gap-2"
        >
          {show3dColumn && (
            <>
              <input
                ref={ifcInputRef}
                type="file"
                accept=".ifc"
                style={{ display: "none" }}
                onChange={(e) => {
                  const picked = e.target.files?.[0];
                  e.target.value = "";
                  if (picked) void handleUploadIfc(picked);
                }}
              />
              <Button
                onClick={() => ifcInputRef.current?.click()}
                loading={ifcUploading}
                variant="default"
              >
                {t("designBOM.uploadIfc") || "上傳ifc檔"}
              </Button>
              {ifcAnalysis.status && (
                <span
                  className="self-center whitespace-nowrap text-xs"
                  style={{
                    color:
                      ifcAnalysis.status === IFC_STATUS_FAILED
                        ? "#ff4d4f"
                        : ifcAnalysis.status === IFC_STATUS_DONE
                          ? "#52c41a"
                          : "#8c8c8c",
                  }}
                  title={ifcAnalysis.error}
                >
                  {ifcAnalysis.status === IFC_STATUS_PROCESSING &&
                    (t("designBOM.ifcAnalyzing") || "IFC 分析中…")}
                  {ifcAnalysis.status === IFC_STATUS_DONE &&
                    (t("designBOM.ifcAnalyzed") || "IFC 分析完成")}
                  {ifcAnalysis.status === IFC_STATUS_FAILED &&
                    (t("designBOM.ifcAnalyzeFailed") || "IFC 分析失敗")}
                </span>
              )}
            </>
          )}
          <Button
            onClick={() => setIsParameterModalOpen(true)}
            variant="default"
          >
            {t("designBOM.exportGuandaId") || "匯出廣達ID"}
          </Button>
          <Button
            onClick={() =>
              navigate(
                `/hrMainPage/addDesignBOM/${id}?quotationId=${searchParams.get("quotationId") || ""}`,
              )
            }
            variant="default"
          >
            {t("designBOM.importComponent") || "匯入構件"}
          </Button>
          <Button
            onClick={() => setIsManualBOMModalOpen(true)}
            variant="default"
          >
            {t("designBOM.manualAddBOM") || "手動新增構件"}
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <div
        className="px-4 py-3 mb-4"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          overflowX: "auto",
          backgroundColor: "#F5F5F5",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: "1 1 0",
          }}
        >
          <span
            style={{
              fontWeight: "500",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("designBOM.guandaId") || "廣達ID"}:
          </span>
          <Input
            size="xs"
            placeholder={t("designBOM.searchGuandaId") || "搜尋廣達ID"}
            value={filters.guandaId}
            onChange={(e) => handleFilterChange("guandaId", e.target.value)}
            style={{ flex: 1, minWidth: "60px" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: "1 1 0",
          }}
        >
          <span
            style={{
              fontWeight: "500",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("designBOM.componentCode") || "構件編號"}:
          </span>
          <Input
            size="xs"
            placeholder={t("designBOM.searchComponentCode") || "搜尋構件編號"}
            value={filters.steelAssemblyCode}
            onChange={(e) =>
              handleFilterChange("steelAssemblyCode", e.target.value)
            }
            style={{ flex: 1, minWidth: "60px" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: "1 1 0",
          }}
        >
          <span
            style={{
              fontWeight: "500",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("designBOM.distinction") || "區別"}:
          </span>
          <Input
            size="xs"
            placeholder={t("designBOM.searchDistinction") || "搜尋區別"}
            value={filters.distinction}
            onChange={(e) => handleFilterChange("distinction", e.target.value)}
            style={{ flex: 1, minWidth: "60px" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: "1 1 0",
          }}
        >
          <span
            style={{
              fontWeight: "500",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("designBOM.transportNumber") || "拆運號碼"}:
          </span>
          <Input
            size="xs"
            placeholder={t("designBOM.searchTransportNumber") || "搜尋拆運號碼"}
            value={filters.transportNumber}
            onChange={(e) =>
              handleFilterChange("transportNumber", e.target.value)
            }
            style={{ flex: 1, minWidth: "60px" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: "1 1 0",
          }}
        >
          <span
            style={{
              fontWeight: "500",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("designBOM.sectionSpecification") || "斷面規格"}:
          </span>
          <Input
            size="xs"
            placeholder={t("designBOM.searchSectionSpec") || "搜尋斷面規格"}
            value={filters.metricSizeMark}
            onChange={(e) =>
              handleFilterChange("metricSizeMark", e.target.value)
            }
            style={{ flex: 1, minWidth: "60px" }}
          />
        </div>

        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Button onClick={applyFilters} variant="filled" size="xs">
            {t("designBOM.search") || "搜尋"}
          </Button>
          <Button onClick={clearFilters} variant="default" size="xs">
            {t("designBOM.clear") || "清除"}
          </Button>
        </div>

        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div style={{ width: 320 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <a onClick={() => updateHiddenColumns([])}>
                  {t("designBOM.showAllColumns")}
                </a>
                <a
                  onClick={() =>
                    updateHiddenColumns(toggleableColumns.map((col) => col.key))
                  }
                >
                  {t("designBOM.hideAllColumns")}
                </a>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px 12px",
                  maxHeight: 360,
                  overflowY: "auto",
                }}
              >
                {toggleableColumns.map((col) => (
                  <Checkbox
                    key={col.key}
                    checked={!hiddenColumns.includes(col.key)}
                    onChange={(e) =>
                      updateHiddenColumns(
                        e.target.checked
                          ? hiddenColumns.filter((k) => k !== col.key)
                          : [...hiddenColumns, col.key],
                      )
                    }
                  >
                    {col.title || col.key}
                  </Checkbox>
                ))}
              </div>
            </div>
          }
        >
          <Button variant="default" size="xs" style={{ flexShrink: 0 }}>
            {t("designBOM.showColumns") || "顯示欄位"} (
            {
              toggleableColumns.filter(
                (col) => !hiddenColumns.includes(col.key),
              ).length
            }
            /{toggleableColumns.length})
          </Button>
        </Popover>
      </div>

      <div
        className="relative"
        style={
          {
            "--ant-pagination-input-pointer-events": "none",
            "--ant-pagination-input-cursor": "pointer",
            "--ant-pagination-input-caret": "transparent",
          } as React.CSSProperties
        }
      >
        <DndContext
          sensors={sensors}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={columnOrder}
            strategy={horizontalListSortingStrategy}
          >
            <Table
              rowSelection={rowSelection}
              scroll={{ x: "max-content", y: 600 }}
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({ id: col.key }),
              }))}
              dataSource={fileUpload}
              loading={tableLoading}
              components={{
                header: {
                  cell: DraggableHeaderCell,
                },
              }}
              pagination={{
                current: currentPage,
                pageSize,
                total: designBOM?.meta?.pagination?.total,
                // antd fires onChange for both page and size changes; handling
                // both here avoids two updateURL calls clobbering each other.
                onChange: (page, size) => {
                  const nextPage = size !== pageSize ? 1 : page;
                  setPageSize(size);
                  setCurrentPage(nextPage);
                  updateURL(undefined, nextPage, size);
                  fetchData(nextPage, size, false, filters);
                },
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showQuickJumper: false,
              }}
              rowKey="id"
              bordered
              sticky
            />
          </SortableContext>
        </DndContext>
      </div>

      <Modal
        title={t("designBOM.parameterSettings") || "參數設定"}
        open={isParameterModalOpen}
        onCancel={handleModalClose}
        width={800}
        footer={[
          <Space
            key="footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Space>
              <Button onClick={handleModalClose} disabled={isExportLoading}>
                {t("designBOM.cancel") || "取消"}
              </Button>
              <Button
                onClick={handleParameterSubmit}
                loading={isExportLoading}
                disabled={isExportLoading}
              >
                {t("designBOM.download") || "下載"}
              </Button>
            </Space>
          </Space>,
        ]}
      >
        <div style={{ padding: "20px 0" }}>
          {/* Parameter 1 */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>
              {t("designBOM.paramA") || "參數A - 車次"} (
              {(() => {
                const firstData = deleteFileUpload[0];
                if (!firstData) return t("designBOM.noData") || "無資料";

                return (
                  [
                    param1Type1 === "char"
                      ? param1Char1
                      : param1Type1 === "none"
                        ? ""
                        : firstData?.[param1Type1] || "",
                    param1Type2 === "char"
                      ? param1Char2
                      : param1Type2 === "none"
                        ? ""
                        : firstData?.[param1Type2] || "",
                    param1Type3 === "char"
                      ? param1Char3
                      : param1Type3 === "none"
                        ? ""
                        : firstData?.[param1Type3] || "",
                  ]
                    .filter(Boolean)
                    .join(" ") || "無資料"
                );
              })()}
              )
            </h3>

            <Space wrap>
              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>
                  {t("designBOM.option1") || "選項1"}
                </div>
                <Select
                  value={param1Type1}
                  onChange={handleParamChange(setParam1Type1)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: t("designBOM.none") || "無" },
                    {
                      value: "transportNumber",
                      label: t("designBOM.train") || "車次",
                    },
                    {
                      value: "distinction",
                      label: t("designBOM.distinction") || "區別",
                    },
                    {
                      value: "installationLocation",
                      label: t("designBOM.installationLocation") || "安裝位置",
                    },
                    {
                      value: "char",
                      label: t("designBOM.character") || "字元",
                    },
                  ]}
                />
                {param1Type1 === "char" && (
                  <Input
                    value={param1Char1}
                    onChange={(e) => setParam1Char1(e.target.value)}
                    placeholder={t("designBOM.enterCharacter") || "輸入字元"}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>
                  {t("designBOM.option2") || "選項2"}
                </div>
                <Select
                  value={param1Type2}
                  onChange={handleParamChange(setParam1Type2)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: t("designBOM.none") || "無" },
                    {
                      value: "transportNumber",
                      label: t("designBOM.train") || "車次",
                    },
                    {
                      value: "distinction",
                      label: t("designBOM.distinction") || "區別",
                    },
                    {
                      value: "installationLocation",
                      label: t("designBOM.installationLocation") || "安裝位置",
                    },
                    {
                      value: "char",
                      label: t("designBOM.character") || "字元",
                    },
                  ]}
                />
                {param1Type2 === "char" && (
                  <Input
                    value={param1Char2}
                    onChange={(e) => setParam1Char2(e.target.value)}
                    placeholder={t("designBOM.enterCharacter") || "輸入字元"}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>
                  {t("designBOM.option3") || "選項3"}
                </div>
                <Select
                  value={param1Type3}
                  onChange={handleParamChange(setParam1Type3)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: t("designBOM.none") || "無" },
                    {
                      value: "transportNumber",
                      label: t("designBOM.train") || "車次",
                    },
                    {
                      value: "distinction",
                      label: t("designBOM.distinction") || "區別",
                    },
                    {
                      value: "installationLocation",
                      label: t("designBOM.installationLocation") || "安裝位置",
                    },
                    {
                      value: "char",
                      label: t("designBOM.character") || "字元",
                    },
                  ]}
                />
                {param1Type3 === "char" && (
                  <Input
                    value={param1Char3}
                    onChange={(e) => setParam1Char3(e.target.value)}
                    placeholder={t("designBOM.enterCharacter") || "輸入字元"}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </Space>
          </div>

          <Divider />

          {/* Parameter 2 */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>
              {t("designBOM.paramB") || "參數B - 構件編號"} (
              {(() => {
                const firstData = deleteFileUpload[0];
                if (!firstData) return t("designBOM.noData") || "無資料";

                return (
                  [
                    param2Type1 === "char"
                      ? param2Char1
                      : param2Type1 === "none"
                        ? ""
                        : firstData?.[param2Type1] || "",
                    param2Type2 === "char"
                      ? param2Char2
                      : param2Type2 === "none"
                        ? ""
                        : firstData?.[param2Type2] || "",
                    param2Type3 === "char"
                      ? param2Char3
                      : param2Type3 === "none"
                        ? ""
                        : firstData?.[param2Type3] || "",
                    param2Type4 === "char"
                      ? param2Char4
                      : param2Type4 === "none"
                        ? ""
                        : firstData?.[param2Type4] || "",
                    param2Type5 === "char"
                      ? param2Char5
                      : param2Type5 === "none"
                        ? ""
                        : firstData?.[param2Type5] || "",
                  ]
                    .filter(Boolean)
                    .join(" ") || "無資料"
                );
              })()}
              )
            </h3>
            <Space wrap>
              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>選項1</div>
                <Select
                  value={param2Type1}
                  onChange={handleParamChange(setParam2Type1)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: "無" },
                    { value: "steelAssemblyCode", label: "構件編號" },
                    //{ value: "part", label: "零件編號" },
                    { value: "transportNumber", label: "車次" },
                    { value: "distinction", label: "區別" },
                    { value: "char", label: "字元" },
                  ]}
                />
                {param2Type1 === "char" && (
                  <Input
                    value={param2Char1}
                    onChange={(e) => setParam2Char1(e.target.value)}
                    placeholder="輸入字元"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>選項2</div>
                <Select
                  value={param2Type2}
                  onChange={handleParamChange(setParam2Type2)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: "無" },
                    { value: "steelAssemblyCode", label: "構件編號" },
                    //{ value: "part", label: "零件編號" },
                    { value: "transportNumber", label: "車次" },
                    { value: "distinction", label: "區別" },
                    { value: "char", label: "字元" },
                  ]}
                />
                {param2Type2 === "char" && (
                  <Input
                    value={param2Char2}
                    onChange={(e) => setParam2Char2(e.target.value)}
                    placeholder="輸入字元"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>選項3</div>
                <Select
                  value={param2Type3}
                  onChange={handleParamChange(setParam2Type3)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: "無" },
                    { value: "steelAssemblyCode", label: "構件編號" },
                    //{ value: "part", label: "零件編號" },
                    { value: "transportNumber", label: "車次" },
                    { value: "distinction", label: "區別" },
                    { value: "char", label: "字元" },
                  ]}
                />
                {param2Type3 === "char" && (
                  <Input
                    value={param2Char3}
                    onChange={(e) => setParam2Char3(e.target.value)}
                    placeholder="輸入字元"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>選項4</div>
                <Select
                  value={param2Type4}
                  onChange={handleParamChange(setParam2Type4)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: "無" },
                    { value: "steelAssemblyCode", label: "構件編號" },
                    //{ value: "part", label: "零件編號" },
                    { value: "transportNumber", label: "車次" },
                    { value: "distinction", label: "區別" },
                    { value: "char", label: "字元" },
                  ]}
                />
                {param2Type4 === "char" && (
                  <Input
                    value={param2Char4}
                    onChange={(e) => setParam2Char4(e.target.value)}
                    placeholder="輸入字元"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <div style={{ width: 200 }}>
                <div style={{ marginBottom: 8 }}>選項5</div>
                <Select
                  value={param2Type5}
                  onChange={handleParamChange(setParam2Type5)}
                  style={{ width: "100%" }}
                  options={[
                    { value: "none", label: "無" },
                    { value: "steelAssemblyCode", label: "構件編號" },
                    //{ value: "part", label: "零件編號" },
                    { value: "transportNumber", label: "車次" },
                    { value: "distinction", label: "區別" },
                    { value: "char", label: "字元" },
                  ]}
                />
                {param2Type5 === "char" && (
                  <Input
                    value={param2Char5}
                    onChange={(e) => setParam2Char5(e.target.value)}
                    placeholder="輸入字元"
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </Space>
          </div>

          <Divider />

          {/* Parameter 3 */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>
              {t("designBOM.paramC") || "參數C"} (
              {printQRCode
                ? t("designBOM.printQRCode") || "印出QR Code"
                : t("designBOM.notPrintQRCode") || "不印出QR Code"}
              )
            </h3>
            <Space>
              <Switch checked={printQRCode} onChange={setPrintQRCode} />
              <span>
                {t("designBOM.shouldPrintQRCode") || "是否要印出QR Code"}
              </span>
            </Space>
          </div>
        </div>
      </Modal>

      <Modal
        title={t("designBOM.manualAddBOMTitle") || "手動新增構件"}
        open={isManualBOMModalOpen}
        onCancel={handleManualBOMModalClose}
        width={800}
        footer={[
          <Space key="footer">
            <Button onClick={handleManualBOMModalClose}>
              {t("designBOM.cancel") || "取消"}
            </Button>
            <Button onClick={handleManualBOMSubmit} variant="filled">
              {t("designBOM.add") || "新增"}
            </Button>
          </Space>,
        ]}
      >
        <div style={{ padding: "20px 0" }}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>構件ID</div>
                <Input
                  placeholder="請輸入構件ID"
                  value={manualBOMForm.steelAssemblyId}
                  onChange={(e) =>
                    handleManualBOMFormChange("steelAssemblyId", e.target.value)
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>構件編號</div>
                <Input
                  placeholder="請輸入構件編號"
                  value={manualBOMForm.steelAssemblyCode}
                  onChange={(e) =>
                    handleManualBOMFormChange(
                      "steelAssemblyCode",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>安裝位置</div>
                <Input
                  placeholder="請輸入安裝位置"
                  value={manualBOMForm.installationLocation}
                  onChange={(e) =>
                    handleManualBOMFormChange(
                      "installationLocation",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>安裝高程</div>
                <Input
                  placeholder="請輸入安裝高程"
                  value={manualBOMForm.installationElevation}
                  onChange={(e) =>
                    handleManualBOMFormChange(
                      "installationElevation",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>區別</div>
                <Input
                  placeholder="請輸入區別"
                  value={manualBOMForm.distinction}
                  onChange={(e) =>
                    handleManualBOMFormChange("distinction", e.target.value)
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>拆運號碼</div>
                <Input
                  placeholder="請輸入拆運號碼"
                  value={manualBOMForm.transportNumber}
                  onChange={(e) =>
                    handleManualBOMFormChange("transportNumber", e.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>拆運說明</div>
                <Input
                  placeholder="請輸入拆運說明"
                  value={manualBOMForm.transportDescription}
                  onChange={(e) =>
                    handleManualBOMFormChange(
                      "transportDescription",
                      e.target.value,
                    )
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>圖面名稱</div>
                <Input
                  placeholder="請輸入圖面名稱"
                  value={manualBOMForm.drawingName}
                  onChange={(e) =>
                    handleManualBOMFormChange("drawingName", e.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>總寬度</div>
                <Input
                  placeholder="請輸入總寬度"
                  value={manualBOMForm.totalWidth}
                  onChange={(e) =>
                    handleManualBOMFormChange("totalWidth", e.target.value)
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>總高度</div>
                <Input
                  placeholder="請輸入總高度"
                  value={manualBOMForm.totalHeight}
                  onChange={(e) =>
                    handleManualBOMFormChange("totalHeight", e.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>總長度</div>
                <Input
                  placeholder="請輸入總長度"
                  value={manualBOMForm.totalLength}
                  onChange={(e) =>
                    handleManualBOMFormChange("totalLength", e.target.value)
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>總重量</div>
                <Input
                  placeholder="請輸入總重量"
                  value={manualBOMForm.totalWeight}
                  onChange={(e) =>
                    handleManualBOMFormChange("totalWeight", e.target.value)
                  }
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>總面積</div>
                <Input
                  placeholder="請輸入總面積"
                  value={manualBOMForm.totalArea}
                  onChange={(e) =>
                    handleManualBOMFormChange("totalArea", e.target.value)
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 8 }}>斷面規格</div>
                <Input
                  placeholder="請輸入斷面規格"
                  value={manualBOMForm.metricSizeMark}
                  onChange={(e) =>
                    handleManualBOMFormChange("metricSizeMark", e.target.value)
                  }
                />
              </div>
            </div>
          </Space>
        </div>
      </Modal>
    </div>
  );
}

export default DesignBOM;
